import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Wand2, Ruler, Layers, IndianRupee, Download } from 'lucide-react';

export const AIArchitect = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Ruler, title: 'State-calibrated estimate', desc: 'Plug in plot size & rooms — get an instant cost calibrated to 2026 state rates.' },
    { icon: Layers, title: 'Full material BOM', desc: 'Cement, bricks, sand, steel, paint — quantities listed, ready to share with contractors.' },
    { icon: IndianRupee, title: 'Order printed blueprint', desc: 'Choose Modern (CAD) or Manual (hand-drawn). Both ship with a 3D Home View.' },
    { icon: Download, title: 'Architect call included', desc: 'Schedule a call with a senior architect — included free with every order.' },
  ];

  return (
    <section
      id="ai-architect"
      className="py-24 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950"
      data-testid="ai-architect-section"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: pitch */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/15 border border-emerald-400/30 rounded-full mb-5">
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-200">
                New · Architect Studio
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
              Estimate. Design. Build.
              <span className="block text-amber-300">All in one place.</span>
            </h2>
            <p className="text-emerald-50/85 text-lg leading-relaxed mb-7 max-w-xl">
              Tell us your plot size and how many rooms you want. We&rsquo;ll compute a state-calibrated
              cost estimate instantly — then you can order a <strong>Modern</strong> or <strong>Manual</strong> printed
              blueprint with a photo-real 3D home view.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-xl">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 hover:border-emerald-400/40 transition-colors"
                >
                  <Icon size={20} className="text-amber-300 mb-2" />
                  <div className="text-white font-semibold text-sm mb-0.5">{title}</div>
                  <div className="text-emerald-50/70 text-xs leading-snug">{desc}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/ai-architect')}
              className="btn-am-accent text-base px-8 py-4"
              data-testid="ai-architect-cta"
            >
              <Wand2 size={18} />
              Get my estimate — Free
            </button>
            <p className="text-emerald-100/50 text-xs mt-3">
              Instant cost · Order printed blueprint &amp; 3D view · Senior architect call included
            </p>
          </div>

          {/* Right: stylised blueprint preview */}
          <div className="hidden lg:block relative">
            <div className="relative bg-gradient-to-br from-[#0a2c4d] to-[#0e3a64] rounded-2xl p-6 border border-emerald-400/20 shadow-2xl">
              <div
                className="absolute inset-0 opacity-30 rounded-2xl pointer-events-none"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.18) 1px, transparent 1px)',
                  backgroundSize: '24px 24px',
                }}
              />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold tracking-[0.2em] text-emerald-300/80 uppercase">
                    Sample Blueprint
                  </span>
                  <span className="text-[10px] text-amber-300/80 font-mono">40' × 30'</span>
                </div>
                <div className="aspect-square bg-[#0a2647] rounded-xl border border-emerald-400/30 grid grid-cols-3 gap-1 p-3">
                  {['LIVING', 'KITCHEN', 'POOJA', 'BED 1', 'BATH', 'BED 2', 'GARAGE', 'GARDEN', 'BED 3'].map((label) => (
                    <div
                      key={label}
                      className="border border-emerald-300/40 rounded flex items-center justify-center text-[9px] font-mono text-emerald-100/80 tracking-wider"
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-amber-300 font-bold text-lg">1,560</div>
                    <div className="text-[9px] uppercase tracking-wider text-emerald-100/60">Sq Ft</div>
                  </div>
                  <div>
                    <div className="text-amber-300 font-bold text-lg">₹22 L</div>
                    <div className="text-[9px] uppercase tracking-wider text-emerald-100/60">Est. Cost</div>
                  </div>
                  <div>
                    <div className="text-amber-300 font-bold text-lg">3 BHK</div>
                    <div className="text-[9px] uppercase tracking-wider text-emerald-100/60">2 Floors</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIArchitect;
